import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import MasonryGallery from '@/components/MasonryGallery';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Matrimoni = () => {
  const { language } = useLanguage();
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery?category=wedding`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  return (
    <div data-testid="matrimoni-page">
      {/* Hero Section - Editorial style */}
      <section className="pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
                {t('weddings.title', language)}
              </p>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
                data-testid="hero-title"
              >
                {t('weddings.subtitle', language)}
              </h1>
              <p className="text-lg leading-relaxed text-muted-foreground mb-8 max-w-xl">
                {t('weddings.description', language)}
              </p>
              <Link to="/contatti">
                <Button size="lg" data-testid="cta-button">
                  {language === 'it' ? 'Raccontami del vostro matrimonio' : 'Tell me about your wedding'}
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/opi5j44w_Varie-5.webp"
                alt="Wedding photography"
                className="w-full h-auto object-cover rounded-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Il Mio Approccio' : 'My Approach'}
          </p>
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {language === 'it'
              ? 'Al vostro fianco, in ogni momento'
              : 'By your side, every moment'}
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground max-w-prose mx-auto">
            {language === 'it'
              ? 'Un matrimonio si vive una volta sola, ed è importante poterlo vivere senza pensare alla fotocamera. Non sono solo il fotografo che racconta la vostra giornata: vi accompagno con discrezione, vi aiuto a sentirvi a vostro agio e creo con voi un rapporto di fiducia. È proprio da quella serenità che nascono le immagini più autentiche e i ricordi che dureranno per sempre.'
              : 'A wedding is experienced only once, and it\'s important to live it without thinking about the camera. I\'m not just the photographer documenting your day: I accompany you discreetly, help you feel comfortable, and build a relationship of trust with you. It\'s from that serenity that the most authentic images and memories that will last forever are born.'}
          </p>
        </motion.div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <MasonryGallery images={galleryImages} />
        </section>
      )}

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {language === 'it'
              ? 'Pronti a raccontare la vostra storia?'
              : 'Ready to tell your story?'}
          </h2>
          <Link to="/contatti">
            <Button size="lg" data-testid="cta-button-weddings">
              {t('weddings.cta', language)}
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Matrimoni;
